import type {
  Action,
  Diagram,
  EMRALD_Model,
  Event,
  ExtSim,
  Group,
  LogicNode,
  State,
  Variable,
} from '@/types/EMRALD_Model';
import type { MainItemType } from '@/types/ModelUtils';
import { type MouseEvent, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTemplateContext } from '@/contexts/TemplateContext';
import { useWindowContext } from '@/contexts/WindowContext';
import { updateSpecifiedModel } from '@/utils/UpdateModel';

interface TemplatedItem {
  type: MainItemType;
  displayType: string;
  locked: boolean;
  oldName: string;
  action: string;
  newName: string;
  exclude: boolean;
  required: boolean;
  emraldItem: Action | Diagram | LogicNode | ExtSim | Event | State | Variable;
}

export function useTemplateForm(templatedData: EMRALD_Model) {
  const {
    groups,
    temporaryTemplates,
    createTemplate,
    findGroupHierarchyByGroupName,
  } = useTemplateContext();
  const [findValue, setFindValue] = useState('');
  const [replaceValue, setReplaceValue] = useState('');
  const [templatedItems, setTemplatedItems] = useState<TemplatedItem[]>([]);
  const [templateName, setTemplateName] = useState('');
  const [templateDesc, setTemplateDesc] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [groupType, setGroupType] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [currentGroup, setCurrentGroup] = useState<Group>();
  const [duplicateNameError, setDuplicateNameError] = useState(false);
  const [groupList, setGroupList] = useState(groups);
  const { handleClose } = useWindowContext();
  const [expanded, setExpanded] = useState(['Group 1']);
  const [diagramStates, setDiagramStates] = useState(
    templatedData.DiagramList[0]?.states,
  );

  useEffect(() => {
    setTemplatedItems(convertModelToArray(templatedData));
  }, [templatedData]);

  /** Build out the templated items array **/
  const convertModelToArray = (model: EMRALD_Model) => {
    const items: TemplatedItem[] = [];
    for (const diagram of model.DiagramList) {
      items.push({
        type: 'Diagram',
        displayType: 'Diagram',
        locked: true,
        oldName: diagram.name,
        newName: diagram.name,
        action: 'keep',
        exclude: false,
        required: false,
        emraldItem: diagram,
      });
      setDiagramStates(diagram.states);
    }
    for (const logicNode of model.LogicNodeList) {
      items.push({
        type: 'LogicNode',
        displayType: 'Logic Node',
        locked: false,
        oldName: logicNode.name,
        newName: logicNode.name,
        action: 'keep',
        exclude: false,
        required: false,
        emraldItem: logicNode,
      });
    }
    for (const extSim of model.ExtSimList) {
      items.push({
        type: 'ExtSim',
        displayType: 'External Sim',
        locked: false,
        oldName: extSim.name,
        newName: extSim.name,
        action: 'keep',
        exclude: false,
        required: false,
        emraldItem: extSim,
      });
    }
    for (const action of model.ActionList) {
      items.push({
        type: 'Action',
        displayType: 'Action',
        locked: false,
        oldName: action.name,
        newName: action.name,
        action: 'keep',
        exclude: false,
        required: false,
        emraldItem: action,
      });
    }
    for (const event of model.EventList) {
      items.push({
        type: 'Event',
        displayType: 'Event',
        locked: false,
        oldName: event.name,
        newName: event.name,
        action: 'keep',
        exclude: false,
        required: false,
        emraldItem: event,
      });
    }
    for (const state of model.StateList) {
      items.push({
        type: 'State',
        displayType: 'State',
        locked: true,
        oldName: state.name,
        newName: state.name,
        action: 'keep',
        exclude: !checkIfDiagramDirectState(state.name),
        required: false,
        emraldItem: state,
      });
    }
    for (const variable of model.VariableList) {
      items.push({
        type: 'Variable',
        displayType: 'Variable',
        locked: false,
        oldName: variable.name,
        newName: variable.name,
        action: 'keep',
        exclude: false,
        required: false,
        emraldItem: variable,
      });
    }
    return items.toSorted((a, b) => {
      if (a.type === 'Diagram') {
        return -1;
      }
      if (a.type === 'State' && b.type !== 'Diagram') {
        return -1;
      }
      if (a.type === 'Event' && b.type !== 'Diagram' && b.type !== 'State') {
        return -1;
      }
      if (
        a.type === 'Action'
        && b.type !== 'Diagram'
        && b.type !== 'State'
        && b.type !== 'Event'
      ) {
        return -1;
      }
      return 1;
    });
  };

  const checkIfDiagramDirectState = (stateName: string) =>
    diagramStates?.includes(stateName);

  const addNewGroup = () => {
    setGroupList(prevGroups => {
      const newGroup: Group = {
        name: newGroupName,
        subgroup: [],
      };
      if (currentGroup?.subgroup) {
        currentGroup.subgroup.push(newGroup);
        toggleExpand(currentGroup.name);
      } else {
        prevGroups.push(newGroup);
      }
      localStorage.setItem('templateGroups', JSON.stringify([...prevGroups]));
      return [...prevGroups];
    });
    setCurrentGroup(undefined);
    setNewGroupName('');
    setShowGroupDialog(false);
  };

  const deleteGroup = () => {
    setGroupList(prevGroups => {
      const updatedGroups = prevGroups
        .map(group => deleteItem(group, currentGroup?.name ?? ''))
        .filter((group): group is Group => group !== null);
      localStorage.setItem('templateGroups', JSON.stringify(updatedGroups));
      return updatedGroups;
    });
    setCurrentGroup(undefined);
    setShowGroupDialog(false);
  };

  const deleteItem = (item: Group, name: string): Group | null => {
    if (item.name === name) {
      return null; // Return null to indicate this item should be deleted
    } else if (item.subgroup) {
      return {
        ...item,
        subgroup: item.subgroup
          .map(child => deleteItem(child, name))
          .filter((child): child is Group => child !== null), // Type guard to filter out null values,
      };
    }
    return item;
  };

  // Function to check for duplicate name
  const checkDuplicateName = (name: string) => {
    const trimmedName = name.trim();
    if (
      trimmedName
      && groupList.some(group => group.name.trim() === trimmedName)
    ) {
      setDuplicateNameError(true);
      return true;
    }
    setDuplicateNameError(false);
    return false;
  };

  const handleNewGroupNameChange = (name: string) => {
    setNewGroupName(name);
    checkDuplicateName(name);
  };

  const handleShowGroupDialog = (type: string) => {
    setGroupType(type);
    if (type === 'main') {
      setCurrentGroup(undefined);
    }
    if (type === 'main' || type === 'sub') {
      setShowGroupDialog(true);
    }
    if (type === 'delete' && currentGroup) {
      if (currentGroup.subgroup && currentGroup.subgroup.length > 0) {
        setShowGroupDialog(true);
      } else {
        deleteGroup();
      }
    }
    handleMenuClose();
  };

  const toggleExpand = (id: string) => {
    setExpanded(prevExpanded =>
      prevExpanded.includes(id)
        ? prevExpanded.filter(item => item !== id)
        : [...prevExpanded, id],
    );
  };

  const handleContextMenu = (
    event: MouseEvent<HTMLDivElement>,
    group: Group,
  ) => {
    event.preventDefault();
    setCurrentGroup(group);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  /** Manage items in templated items array **/
  const handleNewNameChange = (index: number, newName: string) => {
    const updatedItems = [...templatedItems];
    if (updatedItems[index]) {
      updatedItems[index].newName = newName;
    }
    setTemplatedItems(updatedItems);
  };

  const handleLockChange = (index: number, locked: boolean) => {
    const updatedItems = [...templatedItems];
    if (updatedItems[index]) {
      updatedItems[index].locked = locked;
    }
    setTemplatedItems(updatedItems);
  };

  const lockAll = () => {
    setTemplatedItems(
      templatedItems.map(item => ({
        ...item,
        locked: true,
      })),
    );
  };

  const unlockAll = () => {
    setTemplatedItems(
      templatedItems.map(item => ({
        ...item,
        locked: false,
      })),
    );
  };

  const updateAllUnlocked = (action: string) => {
    setTemplatedItems(
      templatedItems.map(item =>
        item.locked
          ? item
          : item.type === 'State' && templatedData.DiagramList.length > 0
            ? { ...item, action: 'rename' }
            : { ...item, action },
      ),
    );
  };

  const handleActionChange = (index: number, action: string) => {
    const updatedItems = [...templatedItems];
    if (updatedItems[index]) {
      updatedItems[index].action = action;
    }
    setTemplatedItems(updatedItems);
  };

  const handleExcludeChange = (index: number, exclude: boolean) => {
    const updatedItems = [...templatedItems];
    if (updatedItems[index]) {
      updatedItems[index].exclude = exclude;
    }
    setTemplatedItems(updatedItems);
  };

  const handleRequiredChange = (index: number, required: boolean) => {
    const updatedItems = [...templatedItems];
    if (updatedItems[index]) {
      updatedItems[index].required = required;
      if ('required' in updatedItems[index].emraldItem) {
        updatedItems[index].emraldItem.required = required;
      }
    }
    setTemplatedItems(updatedItems);
  };

  const handleApply = () => {
    setTemplatedItems(prevItems => {
      const updatedItems = prevItems.map(item => {
        if (item.newName.includes(findValue) && !item.locked) {
          const newName = item.newName.replace(
            new RegExp(findValue, 'g'),
            replaceValue,
          );
          return {
            ...item,
            newName,
          };
        }
        return item;
      });
      return updatedItems;
    });
  };

  const removeExcludedItems = () => {
    templatedData.DiagramList = templatedData.DiagramList.filter(
      d =>
        !templatedItems.some(
          ti => ti.type === 'Diagram' && ti.exclude && d.name === ti.newName,
        ),
    );
    templatedData.LogicNodeList = templatedData.LogicNodeList.filter(
      d =>
        !templatedItems.some(
          ti => ti.type === 'LogicNode' && ti.exclude && d.name === ti.newName,
        ),
    );
    templatedData.ActionList = templatedData.ActionList.filter(
      d =>
        !templatedItems.some(
          ti => ti.type === 'Action' && ti.exclude && d.name === ti.newName,
        ),
    );
    templatedData.ExtSimList = templatedData.ExtSimList.filter(
      d =>
        !templatedItems.some(
          ti => ti.type === 'ExtSim' && ti.exclude && d.name === ti.newName,
        ),
    );
    templatedData.EventList = templatedData.EventList.filter(
      d =>
        !templatedItems.some(
          ti => ti.type === 'Event' && ti.exclude && d.name === ti.newName,
        ),
    );
    templatedData.StateList = templatedData.StateList.filter(
      d =>
        !templatedItems.some(
          ti => ti.type === 'State' && ti.exclude && d.name === ti.newName,
        ),
    );
    templatedData.VariableList = templatedData.VariableList.filter(
      d =>
        !templatedItems.some(
          ti => ti.type === 'Variable' && ti.exclude && d.name === ti.newName,
        ),
    );
  };

  const handleSave = () => {
    templatedData.id = uuidv4();
    templatedData.name = templateName;
    templatedData.desc = templateDesc;
    templatedData.group = findGroupHierarchyByGroupName(groups, selectedGroup);
    removeExcludedItems(); // Remove excluded items before building the template

    // Go through all of the renamed items and update the pasted model
    for (const item of templatedItems) {
      if (!item.exclude) {
        const itemCopy = structuredClone(item.emraldItem);
        if (item.action === 'rename') {
          itemCopy.name = item.newName;
        }
        updateSpecifiedModel(itemCopy, item.type, templatedData, false);
        const updatedItems = convertModelToArray(templatedData);
        const updated = updatedItems.find(
          ui => ui.type === item.type && ui.oldName === item.newName,
        );
        if (updated) {
          item.emraldItem = updated.emraldItem;
          item.emraldItem.id = uuidv4();
        }
      }
    }

    // Proceed with creating the template and closing the window
    createTemplate(templatedData);
    handleClose();
  };
  return {
    findValue,
    replaceValue,
    templatedItems,
    templateName,
    templateDesc,
    selectedGroup,
    anchorEl,
    showGroupDialog,
    groupType,
    newGroupName,
    currentGroup,
    groupList,
    temporaryTemplates,
    expanded,
    duplicateNameError,
    checkIfDiagramDirectState,
    setFindValue,
    setReplaceValue,
    setTemplatedItems,
    setTemplateName,
    setTemplateDesc,
    setSelectedGroup,
    setAnchorEl,
    setShowGroupDialog,
    setGroupType,
    setNewGroupName,
    setCurrentGroup,
    setExpanded,
    handleShowGroupDialog,
    handleMenuClose,
    handleContextMenu,
    handleNewNameChange,
    handleLockChange,
    lockAll,
    unlockAll,
    updateAllUnlocked,
    handleActionChange,
    handleExcludeChange,
    handleRequiredChange,
    handleClose,
    handleApply,
    handleSave,
    addNewGroup,
    deleteGroup,
    deleteItem,
    toggleExpand,
    handleNewGroupNameChange,
    checkDuplicateName,
  };
}
